import React, { useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { CANTONS, cantonByOption, regionSlug } from '../../../util/regions';
import { H4, IconArrowHead, InlineTextButton, NamedLink } from '../../../components';
import { CATEGORIES } from '../../LandingPages/landingContent';

import css from './FooterLinks.module.css';

// The cantons the category pages name in their hero copy, so they stay visible
// while the list is collapsed on phones. The rest follow alphabetically.
const FEATURED_CANTONS = [
  'Zuerich',
  'Bern',
  'Basel-Stadt',
  'Luzern',
  'St-Gallen',
  'Zug',
  'Graubuenden',
  'Tessin',
];

const regionLinks = [
  ...FEATURED_CANTONS.map(cantonByOption),
  ...CANTONS.filter(c => !FEATURED_CANTONS.includes(c.option)),
].map(c => ({
  key: c.option,
  label: c.label,
  name: 'RegionLandingPage',
  params: { slug: regionSlug(c.option) },
}));

const categoryLinks = CATEGORIES.map(c => ({
  key: c.slug,
  label: c.label,
  name: 'CategoryLandingPage',
  params: { slug: c.slug },
}));

const TABS = [
  {
    id: 'region',
    labelId: 'FooterLinks.regionTab',
    links: regionLinks,
    hub: { name: 'RegionHubPage', labelId: 'FooterLinks.allRegions' },
    collapsible: true,
  },
  {
    id: 'category',
    labelId: 'FooterLinks.categoryTab',
    links: categoryLinks,
    hub: { name: 'CategoryHubPage', labelId: 'FooterLinks.allCategories' },
  },
];

/**
 * Links to every canton and category landing page, in two tabs above the footer.
 *
 * Both panels are always rendered and the inactive one only gets `hidden`, so the
 * server-rendered HTML carries every link on every page for crawlers.
 *
 * @component
 * @returns {JSX.Element}
 */
const FooterLinks = () => {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState(TABS[0].id);
  const [expanded, setExpanded] = useState(false);

  const onTabKeyDown = e => {
    const index = TABS.findIndex(t => t.id === activeTab);
    const next = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: TABS.length - 1 }[
      e.key
    ];
    if (next === undefined) {
      return;
    }
    e.preventDefault();
    const tab = TABS[(next + TABS.length) % TABS.length];
    setActiveTab(tab.id);
    document.getElementById(`footerLinksTab-${tab.id}`)?.focus();
  };

  return (
    <section className={css.root} aria-labelledby="footerLinksHeading">
      <div className={css.content}>
        <H4 as="h2" id="footerLinksHeading" className={css.heading}>
          {intl.formatMessage({ id: 'FooterLinks.heading' })}
        </H4>

        <div className={css.tabList} role="tablist" onKeyDown={onTabKeyDown}>
          {TABS.map(tab => {
            const isActive = tab.id === activeTab;
            return (
              <InlineTextButton
                key={tab.id}
                type="button"
                rootClassName={classNames(css.tab, { [css.selectedTab]: isActive })}
                role="tab"
                id={`footerLinksTab-${tab.id}`}
                aria-selected={isActive}
                aria-controls={`footerLinksPanel-${tab.id}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActiveTab(tab.id)}
              >
                {intl.formatMessage({ id: tab.labelId })}
              </InlineTextButton>
            );
          })}
        </div>

        {TABS.map(tab => {
          const listId = `footerLinksList-${tab.id}`;
          return (
            <div
              key={tab.id}
              role="tabpanel"
              id={`footerLinksPanel-${tab.id}`}
              aria-labelledby={`footerLinksTab-${tab.id}`}
              hidden={tab.id !== activeTab}
            >
              <ul
                id={listId}
                className={classNames(css.list, { [css.collapsed]: tab.collapsible && !expanded })}
              >
                {tab.links.map(link => (
                  <li key={link.key} className={css.item}>
                    <NamedLink className={css.link} name={link.name} params={link.params}>
                      {link.label}
                    </NamedLink>
                  </li>
                ))}
                <li className={css.item}>
                  <NamedLink className={css.hubLink} name={tab.hub.name}>
                    {intl.formatMessage({ id: tab.hub.labelId })}
                  </NamedLink>
                </li>
              </ul>
              {tab.collapsible ? (
                <InlineTextButton
                  type="button"
                  rootClassName={css.showMore}
                  aria-expanded={expanded}
                  aria-controls={listId}
                  onClick={() => setExpanded(!expanded)}
                >
                  {intl.formatMessage({
                    id: expanded ? 'FooterLinks.showLess' : 'FooterLinks.showMore',
                  })}
                  <IconArrowHead direction={expanded ? 'up' : 'down'} size="small" />
                </InlineTextButton>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default FooterLinks;
